import { useEffect, useState } from 'react'
import { csrfHeader } from '~/utils/csrf'
import { encodeAsJSON, responseAsJSON } from '~/utils/json'
import { randomWait } from '~/utils/wait'

// default object used in the hook to avoid an infinite loop as JS recreates default params on every call
const emptyObject = {}

// use lower values to avoid blocking the tests for too long
const minInterval = process.env.NODE_ENV === 'test' ? 100 : 3000
const maxInterval = process.env.NODE_ENV === 'test' ? 250 : 10000

function isAbortError(err) {
  return err.name === 'AbortError'
}

function fetchWithRetry({ path, params, abortController }) {
  const base = typeof document !== 'undefined' ? document.location.href : 'http://localhost'
  const url = new URL(path, base)
  const request = fetch(url, {
    signal: abortController.signal,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...csrfHeader(),
    },
    body: encodeAsJSON(params),
  })

  const handleError = (err) => {
    // the request was canceled due to an unmount or by the browser, due to a page change
    // in this case, the request should not be retried because the component is gone or is being re-rendered
    if (isAbortError(err)) {
      throw err
    } else {
      // retry with a random interval between min and max interval
      return randomWait(minInterval, maxInterval).then(() => {
        // do not retry if the request was aborted while waiting
        if (abortController.signal.aborted) {
          throw abortController.signal.reason
        }

        return fetchWithRetry({ path, params, abortController })
      })
    }
  }

  return request.then(responseAsJSON).catch(handleError)
}

export default function useRetryablePostJSON({ path, params = emptyObject, initialData = emptyObject }) {
  const [data, setData] = useState(initialData)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)

    const abortController = new AbortController()
    const request = fetchWithRetry({ path, params, abortController })

    request
      .then((data) => setData(data))
      .catch((err) => {
        // ignore the abort error because the url or params have changed, or the page has changed in
        // the browser
        if (!isAbortError(err)) {
          throw err
        }
      })
      .finally(() => {
        // if the request was aborted by the unmount callback, do not touch the
        // loading indicator because other requests are likely in progress
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => {
      // cancel the request on unmount/re-render to avoid updating the state
      // with outdated data
      abortController.abort()
    }
  }, [path, params]) // will fetch again on url or params change

  return { data, isLoading }
}
